/* This example requires Tailwind CSS v2.0+ */
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { parseDigit, templateFormatter, templateParser } from "input-format";
import ReactInput from "input-format/react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import InputTextModal from "../../components/clientServiceSubscriptionTask/modalGeneralComponents/InputTextModal";
import ButtonModal from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/ButtonModal";
import ColoredBadge from "../../components/ColoredBadge";
import { formatTaxNumber } from "../../helpers/formatTaxNumber";
import { TAX_NUMBER_REGEX } from "../../helpers/taxNumberRegex";
import { getClient } from "../../services/getClient";
import { updateClient } from "../../services/updateClient";
import SelectInModal from "../../utils/SelectInModal";
import InputTextAreaModal from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/InputTextAreaModal";

const METHODS_OF_CONTACT = ["Email", "Text", "Phone Call"];
const LANGUAGES = ["English", "Spanish"];
const CLIENT_TYPES = ["INDIVIDUAL", "BUSINESS"];

const SectionCard = ({ title, children }) => (
    <section className="h-full p-4 bg-white border shadow-sm rounded-xl">
        <h2 className="text-sm font-bold text-slate-800">{title}</h2>
        <div className="mt-1">{children}</div>
    </section>
);

const AddressInput = ({ label, value, onChange }) => (
    <div className="mt-5 sm:mt-6">
        <label className="block text-sm font-medium text-left text-gray-700">
            {label}
        </label>
        <div className="relative inline-block w-full text-left">
            <input
                type="text"
                onChange={onChange}
                value={value ?? ""}
                className="inline-flex justify-between w-full px-3 py-2 text-sm font-normal text-gray-700 truncate bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0"
            />
        </div>
    </div>
);

const UpdateClientModal = ({ open, setOpen, client, setClient }) => {
    const [clientForm, setClientForm] = useState(null);
    const completeButtonRef = useRef(null);

    const updateClientForm = (path, value) => {
        setClientForm((prev) => {
            if (!prev) return prev;
            const updatedForm = { ...prev };
            const keys = path.split(".");
            let temp = updatedForm;

            for (let i = 0; i < keys.length - 1; i++) {
                temp[keys[i]] = { ...temp[keys[i]] };
                temp = temp[keys[i]];
            }

            temp[keys[keys.length - 1]] = value;
            return updatedForm;
        });
    };

    useEffect(() => {
        if (!client) return;
        const newClient = {
            ...client,
            address: { ...(client.address ?? {}) },
            teams: Array.isArray(client.teams) ? [...client.teams] : [],
            taxNumber: String(client.taxNumber ?? "").replace(/\D/g, ""),
            clientDetail: {
                ...(client.clientDetail ?? {}),
                spouseTaxNumber: String(
                    client.clientDetail?.spouseTaxNumber ?? ""
                ).replace(/\D/g, ""),
            },
            subscriptionCancellationDate: client.subscriptionCancellationDate
                ? client.subscriptionCancellationDate.split("T")[0]
                : "",
            inactiveReason: client.inactiveReason ?? "", // NEW: initialize reason
        };
        console.log("newClient", newClient);
        setClientForm(newClient);
    }, [client]);

    const onHandleCancel = () => {
        if (!clientForm) return;
        setOpen(false);
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();

        if (!clientForm) return;

        const normalizedInactiveReason =
            clientForm?.isActive === false
                ? clientForm?.inactiveReason?.trim() || null
                : null;

        if (!clientForm.clientName) {
            toast.error("Name is required");
            return;
        }
        if (!clientForm.clientDetail?.clientEmail) {
            toast.error("Email is required");
            return;
        }
        if (!clientForm.clientDetail?.clientType) {
            toast.error("Client type is required");
            return;
        }
        if (
            !TAX_NUMBER_REGEX.test(
                String(clientForm.taxNumber ?? "").replace(/\D/g, "")
            )
        ) {
            toast.error("Tax ID Number has an invalid format");
            return;
        }
        if (clientForm.clientDetail?.clientType === "INDIVIDUAL") {
            clientForm.clientDetail.pointOfContactName = "";
            clientForm.clientDetail.pointOfContactEmail = "";
            clientForm.clientDetail.secondaryPointOfContactName = "";
            clientForm.clientDetail.secondaryPointOfContactEmail = "";
        } else {
            clientForm.clientDetail.spouseName = "";
            clientForm.clientDetail.spouseEmail = "";
            clientForm.clientDetail.spouseTaxNumber = "";
        }

        const clientToUpdate = {
            ...clientForm,
            address: { ...(clientForm.address ?? {}) },
            teams: Array.isArray(clientForm.teams) ? [...clientForm.teams] : [],
            taxNumber: String(clientForm.taxNumber ?? "").replace(/\D/g, ""),
            clientDetail: {
                ...(clientForm.clientDetail ?? {}),
                spouseTaxNumber: String(
                    clientForm.clientDetail?.spouseTaxNumber ?? ""
                ).replace(/\D/g, ""),
            },
            inactiveReason: normalizedInactiveReason,
        };

        const res = await updateClient(clientToUpdate);

        if (res.success) {
            toast.success(res.message);

            const refreshedClientResponse = await getClient(client.id);
            if (refreshedClientResponse.success) {
                setClient(refreshedClientResponse.data);
                setOpen(false);
            } else {
                toast.error(
                    "Client was updated, but the latest client data could not be loaded. Please refresh the page."
                );
            }
        } else {
            toast.error(res.message);
        }
    };

    const onClose = () => {
        if (!clientForm?.manager || !clientForm?.reviewer) {
            toast.error("Please select a manager and a reviewer");
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={() => onClose()}
                    className="relative z-50"
                >
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    <div className="fixed inset-0 w-full overflow-y-auto">
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-screen h-screen px-4 pt-5 pb-4 overflow-y-auto text-left transition-all transform rounded-lg shadow-xl bg-slate-50 sm:px-8 lg:px-16">
                                <div className="text-center">
                                    <div className="flex justify-between w-full">
                                        <Dialog.Title className="w-full text-lg font-medium leading-6 text-center text-gray-900">
                                            Update Client
                                        </Dialog.Title>
                                    </div>
                                </div>

                                <form onSubmit={onHandleSubmit}>
                                    <div className="grid items-stretch grid-cols-1 gap-4 mt-6 lg:grid-cols-12">
                                        <div className="h-full lg:col-span-4">
                                            <SectionCard title="Contact Info">
                                                <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2 lg:grid-cols-1">
                                                    <div className="mt-5 sm:mt-6">
                                                        <label
                                                            htmlFor="phoneNumber"
                                                            className="block text-sm font-medium text-gray-700"
                                                        >
                                                            Phone
                                                        </label>
                                                        <ReactInput
                                                            value={
                                                                clientForm?.phoneNumber
                                                            }
                                                            onChange={(e) =>
                                                                updateClientForm(
                                                                    "phoneNumber",
                                                                    e
                                                                )
                                                            }
                                                            format={templateFormatter(
                                                                "(xxx) xxx-xxxx"
                                                            )}
                                                            parse={templateParser(
                                                                "(xxx) xxx-xxxx",
                                                                parseDigit
                                                            )}
                                                            displayType="input"
                                                            type="text"
                                                            className="inline-flex justify-between w-full px-3 py-2 text-sm font-normal text-gray-700 truncate bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0"
                                                        />
                                                    </div>

                                                    {clientForm?.clientDetail
                                                        ?.clientType ===
                                                    "INDIVIDUAL" ? (
                                                        <>
                                                            <InputTextModal
                                                                required
                                                                label="Email *"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.clientEmail",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                value={
                                                                    clientForm
                                                                        ?.clientDetail
                                                                        ?.clientEmail
                                                                }
                                                            />
                                                            <InputTextModal
                                                                label="Spouse name"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.spouseName",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                value={
                                                                    clientForm
                                                                        ?.clientDetail
                                                                        ?.spouseName
                                                                }
                                                            />
                                                            <InputTextModal
                                                                label="Spouse email"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.spouseEmail",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                value={
                                                                    clientForm
                                                                        ?.clientDetail
                                                                        ?.spouseEmail
                                                                }
                                                            />
                                                            <InputTextModal
                                                                label="Spouse tax#"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.spouseTaxNumber",
                                                                        e.target.value
                                                                            .replace(
                                                                                /\D/g,
                                                                                ""
                                                                            )
                                                                            .slice(
                                                                                0,
                                                                                9
                                                                            )
                                                                    )
                                                                }
                                                                value={formatTaxNumber(
                                                                    {
                                                                        clientType:
                                                                            "INDIVIDUAL",
                                                                        taxNumber:
                                                                            clientForm
                                                                                ?.clientDetail
                                                                                ?.spouseTaxNumber,
                                                                    }
                                                                )}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <InputTextModal
                                                                label="Main contact name"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.pointOfContactName",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                value={
                                                                    clientForm
                                                                        ?.clientDetail
                                                                        ?.pointOfContactName
                                                                }
                                                            />
                                                            <InputTextModal
                                                                label="Main contact email"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.pointOfContactEmail",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                value={
                                                                    clientForm
                                                                        ?.clientDetail
                                                                        ?.pointOfContactEmail
                                                                }
                                                            />
                                                            <InputTextModal
                                                                label="Secondary contact name"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.secondaryPointOfContactName",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                value={
                                                                    clientForm
                                                                        ?.clientDetail
                                                                        ?.secondaryPointOfContactName
                                                                }
                                                            />
                                                            <InputTextModal
                                                                label="Secondary contact email"
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "clientDetail.secondaryPointOfContactEmail",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                value={
                                                                    clientForm
                                                                        ?.clientDetail
                                                                        ?.secondaryPointOfContactEmail
                                                                }
                                                            />
                                                        </>
                                                    )}

                                                    <SelectInModal
                                                        label="Preferred contact"
                                                        required
                                                        options={METHODS_OF_CONTACT}
                                                        value={
                                                            clientForm?.clientDetail
                                                                ?.contactMethod
                                                        }
                                                        onChange={(e) =>
                                                            updateClientForm(
                                                                "clientDetail.contactMethod",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </SectionCard>
                                        </div>

                                        <div className="h-full lg:col-span-8">
                                            <SectionCard title="Client details">
                                                <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                                                    <InputTextModal
                                                        required
                                                        label="Client name *"
                                                        onChange={(e) =>
                                                            updateClientForm(
                                                                "clientName",
                                                                e.target.value
                                                            )
                                                        }
                                                        value={
                                                            clientForm?.clientName
                                                        }
                                                    />

                                                    <SelectInModal
                                                        label="Client type"
                                                        required
                                                        options={CLIENT_TYPES}
                                                        value={
                                                            clientForm?.clientDetail
                                                                ?.clientType
                                                        }
                                                        onChange={(e) =>
                                                            updateClientForm(
                                                                "clientDetail.clientType",
                                                                e.target.value
                                                            )
                                                        }
                                                    />

                                                    <InputTextModal
                                                        required
                                                        label="Tax# *"
                                                        maxLength={9}
                                                        onChange={(e) =>
                                                            updateClientForm(
                                                                "taxNumber",
                                                                e.target.value
                                                                    .replace(
                                                                        /\D/g,
                                                                        ""
                                                                    )
                                                                    .slice(0, 9)
                                                            )
                                                        }
                                                        value={formatTaxNumber({
                                                            clientType:
                                                                clientForm
                                                                    ?.clientDetail
                                                                    ?.clientType,
                                                            taxNumber:
                                                                clientForm?.taxNumber,
                                                        })}
                                                    />

                                                    <SelectInModal
                                                        label="Language"
                                                        required
                                                        options={LANGUAGES}
                                                        value={
                                                            clientForm?.clientDetail
                                                                ?.language
                                                        }
                                                        onChange={(e) =>
                                                            updateClientForm(
                                                                "clientDetail.language",
                                                                e.target.value
                                                            )
                                                        }
                                                    />

                                                    <div className="pt-5 mt-2 border-t sm:col-span-2 border-slate-200">
                                                        <div className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                                                            Address
                                                        </div>
                                                        <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                                                            <AddressInput
                                                                label="Street address"
                                                                value={
                                                                    clientForm
                                                                        ?.address
                                                                        ?.street
                                                                }
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "address.street",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                            <AddressInput
                                                                label="City"
                                                                value={
                                                                    clientForm
                                                                        ?.address
                                                                        ?.city
                                                                }
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "address.city",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                            <AddressInput
                                                                label="State/Province"
                                                                value={
                                                                    clientForm
                                                                        ?.address
                                                                        ?.stateProvince
                                                                }
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "address.stateProvince",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                            <AddressInput
                                                                label="Postal Code"
                                                                value={
                                                                    clientForm
                                                                        ?.address
                                                                        ?.postalCode
                                                                }
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "address.postalCode",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                            <AddressInput
                                                                label="Country"
                                                                value={
                                                                    clientForm
                                                                        ?.address
                                                                        ?.country
                                                                }
                                                                onChange={(e) =>
                                                                    updateClientForm(
                                                                        "address.country",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="pt-5 mt-2 border-t sm:col-span-2 border-slate-200">
                                                        <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                                                            <div className="mt-5 sm:mt-6">
                                                                <label className="block text-sm font-medium text-gray-700">
                                                                    Status
                                                                </label>
                                                                <div className="mt-2">
                                                                    {typeof clientForm?.isActive === "boolean" ? (
                                                                        <ColoredBadge
                                                                            colorClass={
                                                                                clientForm.isActive
                                                                                    ? "bg-teal-500 text-white"
                                                                                    : "bg-red-500 text-white"
                                                                            }
                                                                            text={clientForm.isActive ? "Active" : "Inactive"}
                                                                        />
                                                                    ) : (
                                                                        <span className="text-sm text-slate-500">Not assigned</span>
                                                                    )}
                                                                </div>
                                                                <p className="mt-2 text-xs text-slate-500">
                                                                    Status is automatically determined by the client's services.
                                                                </p>
                                                            </div>

                                                            {clientForm?.isActive ===
                                                                false && (
                                                                <>
                                                                    <div className="mt-5 sm:mt-6">
                                                                        <label className="block text-sm font-medium text-gray-700">
                                                                            Inactive Date
                                                                        </label>
                                                                        <div className="mt-2 text-sm text-slate-800">
                                                                            {clientForm?.subscriptionCancellationDate || "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div className="sm:col-span-2">
                                                                        <InputTextAreaModal
                                                                            label="Inactive reason"
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateClientForm(
                                                                                    "inactiveReason",
                                                                                    e
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            value={
                                                                                clientForm?.inactiveReason ??
                                                                                ""
                                                                            }
                                                                        />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </SectionCard>
                                        </div>
                                    </div>

                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <ButtonModal
                                            name="Save"
                                            type="submit"
                                            className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-500 border border-transparent rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:col-start-2 sm:text-sm"
                                        />

                                        <ButtonModal
                                            name="Cancel"
                                            type="button"
                                            onClick={onHandleCancel}
                                            className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        />
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default UpdateClientModal;
